import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "../../lib/api";
import { Loader2, BarChart3, AlertTriangle, Layers, Wallet, RefreshCw, Edit, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { useCollapse } from "../../lib/cockpitCollapse";

/**
 * Bestands-Übersicht mit Marge-Ampel (Feb-2026, User-Wunsch).
 * Quelle: GET /api/dealer/inventory-margin → { items, stats }.
 * Spalten: Angebotsnummer, Marke/Modell, EK, Einstand, VK B2C, Roh-Marge €/% (+Ampel),
 * Lagerort/Standort, Status. KPI-Kacheln + Filter (Ampel, Standort, Status).
 */
const AMPEL = {
    gruen: { label: "Grün", dot: "bg-emerald-500", text: "text-emerald-700", row: "" },
    gelb: { label: "Gelb", dot: "bg-amber-500", text: "text-amber-700", row: "" },
    rot: { label: "Rot", dot: "bg-red-500", text: "text-red-700", row: "bg-red-50/40" },
};

const STATUS_LABELS = {
    active: "Aktiv",
    reserved: "Reserviert",
    sold: "Verkauft",
    draft: "Entwurf",
    deleted: "Gelöscht",
    inactive: "Inaktiv",
    paused: "Pausiert",
};

function pct(v) {
    if (v == null) return "—";
    return `${v.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`;
}

export default function InventoryMarginCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ampelFilter, setAmpelFilter] = useState(null);
    const [locationFilter, setLocationFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    // Jun-2026 (User-Wunsch) — einklappbar; Zustand geräteübergreifend gespeichert.
    const [collapsed, toggleCollapsed] = useCollapse("inventory_margin", true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/dealer/inventory-margin");
            setData(data);
        } catch {
            setData({ items: [], stats: { total: 0, under_target: 0, avg_margin_pct: 0, total_einstand: 0 } });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const items = data?.items || [];

    const locations = useMemo(() => {
        const set = new Set();
        items.forEach((it) => {
            const loc = it.dispo_location_label || it.location;
            if (loc) set.add(loc);
        });
        return Array.from(set).sort();
    }, [items]);

    const statuses = useMemo(() => {
        const set = new Set();
        items.forEach((it) => it.status && set.add(it.status));
        return Array.from(set).sort();
    }, [items]);

    const filtered = useMemo(() => items.filter((it) => {
        if (ampelFilter && it.ampel !== ampelFilter) return false;
        const loc = it.dispo_location_label || it.location || "";
        if (locationFilter !== "all" && loc !== locationFilter) return false;
        if (statusFilter !== "all" && it.status !== statusFilter) return false;
        return true;
    }), [items, ampelFilter, locationFilter, statusFilter]);

    const stats = data?.stats || {};
    const hasFilters = ampelFilter || locationFilter !== "all" || statusFilter !== "all";

    if (loading) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="inventory-margin-loading">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Bestand wird geladen…</p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="inventory-margin-card">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <button
                    type="button"
                    onClick={toggleCollapsed}
                    className="text-lg font-black uppercase tracking-wider text-[#111827] inline-flex items-center gap-2"
                    data-testid="inventory-margin-toggle"
                    aria-expanded={!collapsed}
                >
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                    <BarChart3 className="w-5 h-5 text-[#FFCC00]" /> Fahrzeuge im Bestand &amp; Marge
                    {stats.under_target > 0 && (
                        <span className="inline-flex items-center h-5 px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-mono-num normal-case tracking-normal" data-testid="inventory-margin-warn-badge">
                            {stats.under_target} unter Marge
                        </span>
                    )}
                </button>
                <Button type="button" variant="outline" size="sm" onClick={load}
                    className="rounded-sm font-bold gap-1.5" data-testid="inventory-margin-refresh">
                    <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
                </Button>
            </div>

            {!collapsed && (<>
            {/* KPI-Kacheln */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="inventory-margin-kpis">
                <div className="bg-[#111827] text-white rounded-sm p-4" data-testid="kpi-total">
                    <div className="text-[10px] uppercase tracking-wider text-[#FFCC00] font-bold inline-flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" /> Fahrzeuge im Bestand
                    </div>
                    <div className="font-mono-num text-3xl font-black leading-tight mt-1">{stats.total ?? 0}</div>
                </div>
                <div className={`rounded-sm p-4 border ${stats.under_target > 0 ? "bg-red-50 border-red-300" : "bg-white border-gray-200"}`} data-testid="kpi-under-target">
                    <div className={`text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 ${stats.under_target > 0 ? "text-red-700" : "text-gray-500"}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> Unter Wunschmarge
                    </div>
                    <div className={`font-mono-num text-3xl font-black leading-tight mt-1 ${stats.under_target > 0 ? "text-red-700" : "text-[#111827]"}`}>{stats.under_target ?? 0}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="kpi-avg-margin">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">
                        <BarChart3 className="w-3.5 h-3.5" /> Ø Roh-Marge
                    </div>
                    <div className="font-mono-num text-3xl font-black leading-tight mt-1 text-[#111827]">{pct(stats.avg_margin_pct)}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="kpi-total-einstand">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">
                        <Wallet className="w-3.5 h-3.5" /> Gesamt-Einstand
                    </div>
                    <div className="font-mono-num text-2xl font-black leading-tight mt-1 text-[#111827]">{formatPrice(stats.total_einstand)}</div>
                </div>
            </div>

            {/* Filter */}
            <div className="bg-white border border-gray-200 rounded-sm p-3 flex items-center gap-2 flex-wrap" data-testid="inventory-margin-filters">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mr-1">Ampel</span>
                {["gruen", "gelb", "rot"].map((key) => {
                    const cfg = AMPEL[key];
                    const active = ampelFilter === key;
                    return (
                        <button key={key} type="button" onClick={() => setAmpelFilter(active ? null : key)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs font-bold uppercase tracking-wider transition-colors ${active ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                            data-testid={`ampel-filter-${key}`}>
                            <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} /> {cfg.label}
                        </button>
                    );
                })}
                <div className="h-6 w-px bg-gray-200 mx-1" />
                <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
                    className="h-8 rounded-sm border border-gray-300 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
                    data-testid="inventory-location-filter">
                    <option value="all">Alle Standorte</option>
                    {locations.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-8 rounded-sm border border-gray-300 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
                    data-testid="inventory-status-filter">
                    <option value="all">Alle Status</option>
                    {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                </select>
                {hasFilters && (
                    <button type="button" onClick={() => { setAmpelFilter(null); setLocationFilter("all"); setStatusFilter("all"); }}
                        className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-[#111827] underline px-2"
                        data-testid="inventory-margin-clear-filter">
                        Filter zurücksetzen
                    </button>
                )}
                <span className="ml-auto text-xs text-gray-500 font-mono-num" data-testid="inventory-margin-count">{filtered.length} / {items.length}</span>
            </div>

            {/* Tabelle */}
            {filtered.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-300 p-12 text-center rounded-sm" data-testid="inventory-margin-empty">
                    <p className="text-gray-600">{items.length === 0 ? "Noch keine Fahrzeuge im Bestand." : "Keine Fahrzeuge für diese Filter-Kombination."}</p>
                    {hasFilters && items.length > 0 && (
                        <Button onClick={() => { setAmpelFilter(null); setLocationFilter("all"); setStatusFilter("all"); }} variant="outline" className="rounded-sm font-bold mt-4">Filter aufheben</Button>
                    )}
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm" data-testid="inventory-margin-table">
                        <thead>
                            <tr className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                                <th className="text-left px-3 py-2">Angebot</th>
                                <th className="text-left px-3 py-2">Marke / Modell</th>
                                <th className="text-right px-3 py-2">EK</th>
                                <th className="text-right px-3 py-2">Einstand</th>
                                <th className="text-right px-3 py-2">VK B2C</th>
                                <th className="text-right px-3 py-2">Roh-Marge</th>
                                <th className="text-left px-3 py-2">Lagerort / Standort</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map((it) => {
                                const cfg = it.ampel ? AMPEL[it.ampel] : null;
                                return (
                                    <tr key={it.id} className={`hover:bg-gray-50 ${cfg?.row || ""}`} data-testid={`inventory-row-${it.id}`}>
                                        <td className="px-3 py-2 font-mono-num text-xs text-gray-600">{it.internal_number || "—"}</td>
                                        <td className="px-3 py-2">
                                            <div className="font-bold text-[#111827]">{[it.brand, it.model].filter(Boolean).join(" ") || it.title || "—"}</div>
                                            {it.title && <div className="text-[11px] text-gray-400 truncate max-w-[200px]">{it.title}</div>}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-num">{formatPrice(it.ek)}</td>
                                        <td className="px-3 py-2 text-right font-mono-num">
                                            {formatPrice(it.einstand)}
                                            {(it.workshop > 0 || it.external > 0) && (
                                                <div className="text-[10px] text-gray-400">
                                                    {it.workshop > 0 && <span>+WS {formatPrice(it.workshop)} </span>}
                                                    {it.external > 0 && <span>+Fremd {formatPrice(it.external)}</span>}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-num">{it.vk_b2c > 0 ? formatPrice(it.vk_b2c) : "—"}</td>
                                        <td className="px-3 py-2 text-right">
                                            <div className={`inline-flex items-center gap-1.5 font-mono-num font-bold ${cfg?.text || "text-gray-400"}`}>
                                                {cfg && <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />}
                                                {it.vk_b2c > 0 ? formatPrice(it.roh_marge) : "—"}
                                            </div>
                                            {it.vk_b2c > 0 && <div className={`text-[11px] ${cfg?.text || "text-gray-400"}`}>{pct(it.marge_pct)}</div>}
                                            {it.target_margin > 0 && (
                                                <div className="text-[10px] text-gray-400">Ziel {formatPrice(it.target_margin)}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-600 max-w-[160px] truncate">{it.dispo_location_label || it.location || "—"}</td>
                                        <td className="px-3 py-2">
                                            <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-gray-100 text-gray-600">{STATUS_LABELS[it.status] || it.status}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Link to={`/edit-listing/${it.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 rounded-sm text-gray-400 hover:text-[#111827] hover:bg-gray-100" title="Bearbeiten" data-testid={`inventory-edit-${it.id}`}>
                                                <Edit className="w-4 h-4" />
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            </>)}
        </div>
    );
}
